import React, { PureComponent } from 'react';
import { compose, withApollo } from 'react-apollo';
import PropTypes from 'prop-types';

import { dictionaryItem } from '../../hocs/withDictionary';
import { mapDictionaryItems } from '../../hocs/withDictionary/utils';

import Modal, { ModalClose, ModalContent } from '../ui/Modal';
import PitchBody from './PitchBody';
import { MessageWarning } from '../ui/Message';
import { RichText, Text, Title } from '../ui';
import Select from '../ui/Form/Select';
import { Label } from '../ui/Form';
import {
  Header,
  HeaderLeft,
  HeaderRight,
} from './AvailabilityPitches.style';
import {
  PITCH_SORT_LABELS,
  PITCH_SORT_TYPES,
} from '../../lib/availability';

import IbePropTypes from '../../IbePropTypes';
import InfoIconWithModal from './InfoIconWithModal';
import Pitches from './modals/Pitches';
import RadioButton from '../ui/RadioButton/RadioButton';
import { AvailabilityPitchesWrapper, PitchesWrapper } from './Availability.style';
import { getDefaultPitch, isSiteFull, mustBookAcrossPitches } from './helpers';
import { SELECTED_DATES_STAY_STATUS } from './constants';
import { AvailabilityContext } from './AvailabilitySearchQuery';

class AvailabilityPitches extends PureComponent {
  static defaultProps = {
    disabled: false,
    error: false,
    pitches: [],
    amend: false,
    start: undefined,
    end: undefined,
    sortType: undefined,
    partyString: '',
    numberOfNights: null,
    pitchErrors: [],
    bookedPitch: '',
    isNeutralCalendar: null,
    pricesLoading: false,
    handlePitchChange: () => {},
  };

  static propTypes = {
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    handlePitchChange: PropTypes.func,
    loading: PropTypes.bool.isRequired,
    memberPricesAreSelected: PropTypes.bool.isRequired,
    pitches: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.pitch)),
    client: PropTypes.shape().isRequired,
    amend: PropTypes.bool,
    start: PropTypes.string,
    end: PropTypes.string,
    sortType: PropTypes.string,
    handleSortTypeChange: PropTypes.func.isRequired,
    partyString: PropTypes.string,
    numberOfNights: PropTypes.number,
    pitchErrors: PropTypes.arrayOf(PropTypes.shape({})),
    bookedPitch: PropTypes.string,
    isNeutralCalendar: PropTypes.string,
    pricesLoading: PropTypes.bool,
  };

  static contextType = AvailabilityContext;

  state = {
    isModalOpen: false,
    hasPitchBeenDefaulted: false,
  }

  componentDidMount() {
    // If we already have the data immediately on mount (e.g. from the cache)
    // it gets applied here
    this.setDefaultPitch();
  }

  componentDidUpdate() {
    // We sometimes have to wait for AvailabilitySearch responses to
    // come back so we can properly set the default pitch
    this.setDefaultPitch();
  }

  setDefaultPitch = () => {
    const {
      amend,
      end,
      handlePitchChange,
      memberPricesAreSelected,
      pitches,
      pricesLoading,
      start,
    } = this.props;

    const { hasPitchBeenDefaulted } = this.state;

    // Don't set a default pitch for amends, that's done elsewhere
    if (amend) return;
    // We need prices to properly default pitches when we have dates
    if (pricesLoading) return;
    // If we've already defaulted the pitch, leave it alone
    if (hasPitchBeenDefaulted) return;

    const hasDates = !!start && !!end;
    const defaultPitch = getDefaultPitch(pitches, hasDates, memberPricesAreSelected);
    handlePitchChange(defaultPitch);

    this.setState({
      hasPitchBeenDefaulted: true,
    });
  }

  handleModalToggle = () => {
    this.setState({ isModalOpen: !this.state.isModalOpen });
  }

  getMinPricePerNightProperty = () => (
    this.props.memberPricesAreSelected ? 'memberMinPricePerNight' : 'nonMemberMinPricePerNight'
  );

  getAvailabilityProperty = () => (
    this.props.memberPricesAreSelected ? 'memberAvailability' : 'nonMemberAvailability'
  );

  render() {
    const {
      amend,
      client,
      disabled,
      error,
      handlePitchChange,
      handleSortTypeChange,
      loading,
      partyString,
      pitches,
      sortType,
      numberOfNights,
      start,
      end,
      pitchErrors,
      bookedPitch,
      memberPricesAreSelected,
      isNeutralCalendar,
    } = this.props;

    const { loading: availabilityLoading } = this.context;

    const minPricePerNightProperty = this.getMinPricePerNightProperty();
    const sortOptions = mapDictionaryItems(client, PITCH_SORT_LABELS);

    const hasSelectedDates = start && end && start !== end;

    // Gathers the pitch availabilityTotal data (date-specific information) for the
    // selected campsite and product type
    const availabilityTotalPitches =
      pitches.map((pitch) => pitch?.availabilityTotalPitchData) ?? [];

    // Returns an enum for if the user needs to book across pitches, etc.
    const stayStatus = mustBookAcrossPitches(
      availabilityTotalPitches,
      memberPricesAreSelected,
    );

    const userMustBookAcrossPitches =
      stayStatus === SELECTED_DATES_STAY_STATUS.MUST_BOOK_ACROSS_PITCHES;

    const pitchesHavePrice =
      availabilityTotalPitches.some((pitch) => !!pitch?.memberAvailabilityTotalPrice);

    const isSiteFullDuringStay = isSiteFull(availabilityTotalPitches, memberPricesAreSelected);

    return (
      <AvailabilityPitchesWrapper data-error={pitchErrors.length > 0}>
        <Modal active={this.state.isModalOpen} overlay>
          <ModalContent>
            <ModalClose onClick={this.handleModalToggle} />
            <RichText
              dictionary={dictionaryItem('AvailabilityPitches', 'Modal')}
            />
          </ModalContent>
        </Modal>

        <Header>
          <HeaderLeft>
            <Title
              dictionary={
                amend
                  ? dictionaryItem('AvailabilityAmend', 'Pitch')
                  : dictionaryItem('AvailabilityPitches')
              }
              tag={1}
              size={4}
            />
            <InfoIconWithModal modalContent={<Pitches />} />
          </HeaderLeft>
          {!amend && (
            <HeaderRight>
              <Label
                dictionary={dictionaryItem('AvailabilityPitches', 'Sort')}
                htmlFor="sort-pitches"
              />
              <Select
                id="sort-pitches"
                name="prefix"
                value={sortType}
                onChange={(e) => {
                  handleSortTypeChange(e.target.value);
                }}
              >
                {Object.values(PITCH_SORT_TYPES).map((pitchSortType) => (
                  <option value={pitchSortType} key={pitchSortType}>
                    {sortOptions[pitchSortType]}
                  </option>
                ))}
              </Select>
            </HeaderRight>
          )}
        </Header>

        {pitches && !pitches.length && (
          <Text
            dictionary={dictionaryItem('AvailabilityPitches', 'NoPitches')}
            align="center"
            marginTop
          />
        )}

        <PitchesWrapper data-testid="pitch-list-wrapper">
          {pitches && pitches.map((pitch) => {
            const { selected, availabilityTotalPitchData } = pitch;
            const {
              hasLowAvailability,
              memberAvailabilityTotalPrice,
              nonMemberAvailabilityTotalPrice,
              daysOnRequest,
              restrictions,
            } = availabilityTotalPitchData ?? {};
            const isDisabled = disabled || loading;
            const minPricePerNight = pitch[minPricePerNightProperty];
            const isSelectedPitchUnavailable = selected && !minPricePerNight;

            const hasRestrictions = restrictions?.length > 0;

            // If we get back any restrictions for this pitch then it is FULL
            const isPitchFullDuringStay = hasRestrictions;

            const isBookedPitch = bookedPitch === pitch.id;

            const totalPrice = memberPricesAreSelected
              ? memberAvailabilityTotalPrice
              : nonMemberAvailabilityTotalPrice;

            const hasDaysOnRequest = daysOnRequest?.length > 0;

            return (
              <RadioButton
                key={pitch.id}
                onClick={() => handlePitchChange(pitch)}
                isSelected={selected}
                isHighlighted={isNeutralCalendar && isBookedPitch}
                isErrored={selected && (isSelectedPitchUnavailable || !pitchesHavePrice)}
              >
                <PitchBody
                  disabled={isDisabled}
                  name={pitch.name}
                  description={pitch.description}
                  minPricePerNight={minPricePerNight}
                  partyString={partyString}
                  numberOfNights={numberOfNights}
                  hasLowAvailability={hasLowAvailability ?? false}
                  isSiteFullDuringStay={hasSelectedDates && isSiteFullDuringStay}
                  isPitchFullDuringStay={hasSelectedDates && isPitchFullDuringStay}
                  isBooked={isBookedPitch}
                  totalPrice={totalPrice}
                  hasRestrictions={hasRestrictions}
                  hasDaysOnRequest={hasDaysOnRequest}
                  id={pitch.id}
                />
              </RadioButton>
            );
          })}
        </PitchesWrapper>

        {!loading && error && (
          <MessageWarning>
            We were not able to find any availability for this campsite at the
            dates provided.
          </MessageWarning>
        )}

        {!availabilityLoading && userMustBookAcrossPitches && !isNeutralCalendar &&
          <MessageWarning dictionary={dictionaryItem('AvailabilityPitches', 'MustBookAcrossPitchesError')} marginTop />
        }
      </AvailabilityPitchesWrapper>
    );
  }
}

export default compose(
  withApollo,
)(AvailabilityPitches);
